import { Platform } from 'quasar'
import { isElectron } from 'src/plugins/helpers/electron'

export const WIDGET_PLATFORMS = {
  HUBSPOT: 'hubspot',
  HUBSPOT_CALLING: 'hubspot-calling',
  HIGHLEVEL: 'highlevel',
  SALESFORCE: 'salesforce'
}

/**
 * Whether a detected platform is one of HubSpot's. Surfaces enabled per integration ask this
 * rather than comparing strings.
 * @param {string|null} platform
 * @returns {boolean}
 */
export function isHubSpotPlatform (platform) {
  return platform === WIDGET_PLATFORMS.HUBSPOT || platform === WIDGET_PLATFORMS.HUBSPOT_CALLING
}

/**
 * Checks if a string contains any platform identifier
 * @param {string} str - The string to check
 * @returns {string|null} 'hubspot', 'salesforce', 'highlevel', or null
 */
function detectPlatformInString (str) {
  if (!str) {
    return null
  }

  const lowerStr = str.toLowerCase()

  if (lowerStr.includes(WIDGET_PLATFORMS.HUBSPOT)) {
    return WIDGET_PLATFORMS.HUBSPOT
  }

  if (lowerStr.includes(WIDGET_PLATFORMS.HIGHLEVEL)) {
    return WIDGET_PLATFORMS.HIGHLEVEL
  }

  if (
    lowerStr.includes(WIDGET_PLATFORMS.SALESFORCE) ||
    lowerStr.includes('.lightning.force.com') ||
    lowerStr.includes('.my.salesforce.com') ||
    lowerStr.includes('.siteforce.com') ||
    lowerStr.includes('.documentforce.com')
  ) {
    return WIDGET_PLATFORMS.SALESFORCE
  }

  return null
}

/**
 * Detects which platform the app is running in (HubSpot, Salesforce, HighLevel)
 * Uses route meta and store state as primary detection methods, with domain-based fallbacks.
 * @param {Object} route - Vue Router route object (optional)
 * @param {Object} store - Vuex store instance (optional, for accessing isSalesforceWidget state)
 * @returns {string|null} 'hubspot', 'salesforce', 'highlevel', or null
 */
export function detectWidgetPlatform (route = null, store = null) {
  // If not in an iframe, not a widget
  // Also skip detection in Electron desktop app
  if (!Platform.within.iframe || isElectron()) {
    return null
  }

  const pathname = (route && route.path) || window.location.pathname || ''
  // New HubSpot Calling app
  if (pathname.includes('/widgets/hubspot-inbound-call-extension')) {
    return WIDGET_PLATFORMS.HUBSPOT_CALLING
  }

  // Legacy HubSpot Widget
  if (pathname.includes('/widgets/hubspot-call-extension')) {
    return WIDGET_PLATFORMS.HUBSPOT
  }

  // Salesforce detection via route meta
  if (route?.matched?.some(r => r.meta?.isSalesforceWidget)) {
    return WIDGET_PLATFORMS.SALESFORCE
  }

  // Salesforce detection via store state
  if (store?.state?.isSalesforceWidget) {
    return WIDGET_PLATFORMS.SALESFORCE
  }

  // Salesforce detection via pathname
  if (pathname.includes('/salesforce/softphone')) {
    return WIDGET_PLATFORMS.SALESFORCE
  }

  try {
    const sources = [
      document.referrer,
      window.location.origin
    ]

    if (window.parent && window.parent !== window) {
      sources.push(window.parent.location.origin)
    }

    for (const source of sources) {
      const platform = detectPlatformInString(source)
      if (platform) {
        return platform
      }
    }
  } catch (e) {
    console.error('Error detecting widget platform', e)
  }

  // Check URL parameters as additional fallback
  try {
    const urlParams = new URLSearchParams(window.location.search)
    const platformParam = urlParams.get('platform')

    if (platformParam && Object.values(WIDGET_PLATFORMS).includes(platformParam.toLowerCase())) {
      return platformParam.toLowerCase()
    }
  } catch (e) {
    // URL params might not be accessible
  }

  return null
}
